import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Phase, PhaseStatus } from './sidebarNav'

interface Props {
  phase: Phase
  status: PhaseStatus
  onClick: () => void
  isExpanded?: boolean
}

export function SidebarPhaseItem({ phase, status, onClick, isExpanded = true }: Props) {
  const isActive = status === 'active'
  const isCompleted = status === 'completed'
  const isUpcoming = status === 'locked' || status === 'available'

  const labelColor = isActive
    ? 'var(--color-text)'
    : isCompleted
      ? 'var(--color-text)'
      : isUpcoming
        ? 'var(--color-text-muted)'
        : 'var(--color-text-secondary)'

  const dotBg = isActive
    ? 'var(--color-text)'
    : isCompleted
      ? 'var(--color-success)'
      : 'var(--color-warm-50)'

  const dotFg = isActive || isCompleted ? '#fff' : 'var(--color-text-muted)'

  if (!isExpanded) {
    return (
      <div className="w-full">
        <button
          type="button"
          onClick={onClick}
          title={`${phase.label}${phase.progress > 0 ? ` · ${phase.progress}%` : ''}`}
          className="w-full flex items-center justify-center py-1.5 rounded-md hover:bg-warm-200/40 transition-colors"
        >
          <span
            className={cn(
              'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-semibold',
              !isActive && !isCompleted && 'border border-warm-200'
            )}
            style={{ backgroundColor: dotBg, color: dotFg }}
          >
            {isCompleted ? <Check className="h-3.5 w-3.5" /> : phase.number}
          </span>
        </button>
        {isActive && phase.progress > 0 && (
          <div aria-hidden className="mx-2.5 mb-0.5 h-[2px] overflow-hidden rounded-sm bg-warm-200">
            <div
              className="h-full rounded-sm bg-accent"
              style={{ width: `${Math.max(0, Math.min(100, phase.progress))}%` }}
            />
          </div>
        )}
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative w-full flex items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors',
        isActive ? 'bg-warm-100' : 'hover:bg-warm-200/40'
      )}
    >
      {isActive && (
        <span
          aria-hidden
          className="absolute left-0 top-2 bottom-2 w-[2px] rounded-sm bg-accent"
        />
      )}
      <span
        className={cn(
          'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-semibold',
          !isActive && !isCompleted && 'border border-warm-200'
        )}
        style={{ backgroundColor: dotBg, color: dotFg }}
      >
        {isCompleted ? <Check className="h-3.5 w-3.5" /> : phase.number}
      </span>
      <span
        className={cn('flex-1 text-[13px] truncate', isActive ? 'font-medium' : 'font-normal')}
        style={{ color: labelColor }}
      >
        {phase.label}
      </span>
      {(isActive || phase.progress > 0) && (
        <span
          className="text-[11px] font-normal flex-shrink-0"
          style={{ color: isActive ? 'var(--color-text-secondary)' : 'var(--color-text-muted)' }}
        >
          {phase.progress}%
        </span>
      )}
      {isActive && phase.progress > 0 && (
        <span
          aria-hidden
          className="absolute left-2.5 right-2.5 bottom-1 h-[2px] overflow-hidden rounded-sm bg-warm-200"
        >
          <span
            className="block h-full bg-accent rounded-sm"
            style={{ width: `${Math.max(0, Math.min(100, phase.progress))}%` }}
          />
        </span>
      )}
    </button>
  )
}

export function SidebarPhaseConnector() {
  return (
    <div className="flex pl-[22px]" aria-hidden>
      <div className="h-2 w-[2px] rounded-sm bg-warm-200" />
    </div>
  )
}
